import React, { useCallback, useEffect, useRef, useState } from 'react';
import { specialThanksCreators } from '../../config/specialThanksCreators';
import { cn } from '../../utils/cn';

interface SpecialThanksBoxProps {
  className?: string;
  variant?: 'default' | 'landing';
}

const SLIDE_PAUSE_MS = 20000;
const SLIDE_SCROLL_DURATION_MS = 1200;

export const SpecialThanksBox: React.FC<SpecialThanksBoxProps> = ({ className, variant = 'default' }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const totalCreators = specialThanksCreators.length;
  const hasMultiple = totalCreators > 1;
  const isLanding = variant === 'landing';
  const pauseTimeoutRef = useRef<number | null>(null);
  const animationTimeoutRef = useRef<number | null>(null);

  const clearTimers = useCallback(() => {
    if (pauseTimeoutRef.current !== null) {
      window.clearTimeout(pauseTimeoutRef.current);
      pauseTimeoutRef.current = null;
    }
    if (animationTimeoutRef.current !== null) {
      window.clearTimeout(animationTimeoutRef.current);
      animationTimeoutRef.current = null;
    }
  }, []);

  const startAutoCycle = useCallback(() => {
    if (!hasMultiple) return;

    clearTimers();

    pauseTimeoutRef.current = window.setTimeout(() => {
      setIsAnimating(true);
      setCurrentIndex((prev) => (prev + 1) % totalCreators);

      animationTimeoutRef.current = window.setTimeout(() => {
        setIsAnimating(false);
        startAutoCycle();
      }, SLIDE_SCROLL_DURATION_MS);
    }, SLIDE_PAUSE_MS);
  }, [clearTimers, hasMultiple, totalCreators]);

  useEffect(() => {
    if (!hasMultiple) return;

    startAutoCycle();

    return () => {
      clearTimers();
    };
  }, [hasMultiple, startAutoCycle, clearTimers]);

  if (!totalCreators) return null;

  const trackStyle: React.CSSProperties = {
    transform: `translateX(-${currentIndex * 100}%)`,
    transition: isAnimating
      ? `transform ${SLIDE_SCROLL_DURATION_MS}ms cubic-bezier(0.16, 1, 0.3, 1)`
      : 'none'
  };

  const handleIndicatorClick = (index: number) => {
    if (!hasMultiple || index === currentIndex) return;

    clearTimers();
    setIsAnimating(true);
    setCurrentIndex(index);

    animationTimeoutRef.current = window.setTimeout(() => {
      setIsAnimating(false);
      startAutoCycle();
    }, SLIDE_SCROLL_DURATION_MS);
  };

  return (
    <div
      className={cn(
        isLanding
          ? 'rounded-xl border border-white/60 bg-white/45 px-4 py-4 shadow-[0_6px_22px_rgba(60,50,30,0.055)] backdrop-blur-2xl dark:border-white/10 dark:bg-white/[0.04] dark:shadow-[0_6px_22px_rgba(0,0,0,0.14)] sm:px-5'
          : 'rounded-lg border border-border/80 bg-background/80 px-4 py-4 shadow-sm sm:px-6',
        className
      )}
    >
      <div className="overflow-hidden">
        <div
          className="flex will-change-transform"
          style={trackStyle}
        >
          {specialThanksCreators.map((creator) => (
            <div key={creator.name} className="w-full flex-shrink-0">
              <div className="w-full flex justify-center">
                <div
                  className={cn(
                    isLanding
                      ? 'flex w-full max-w-[720px] items-center justify-center gap-3 text-left'
                      : 'flex flex-col items-center justify-center gap-2 text-center sm:flex-row sm:items-center sm:justify-center sm:gap-3 sm:text-left'
                  )}
                >
                  <div
                    className={cn(
                      'overflow-hidden rounded-full border bg-muted/40',
                      isLanding
                        ? 'h-12 w-12 shrink-0 border-white/70 shadow-sm dark:border-white/10 sm:h-14 sm:w-14'
                        : 'h-16 w-16 border-border/80'
                    )}
                  >
                    <img src={creator.icon} alt={`${creator.name} icon`} className="h-full w-full object-cover" />
                  </div>
                  <div className={cn('min-w-0', isLanding ? 'max-w-[560px] space-y-1' : 'space-y-0.5 sm:ml-3')}>
                    <p
                      className={cn(
                        'font-semibold text-foreground',
                        isLanding ? 'text-[0.82rem] leading-snug sm:text-sm' : 'text-xs sm:text-sm'
                      )}
                    >
                      <span>{creator.thankYouLine}</span>{' '}
                      <span className="inline-flex items-center gap-1 align-middle">
                        {creator.twitchLink && (
                          <a
                            href={creator.twitchLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center"
                          >
                            <img
                              src={creator.twitchIcon ?? '/assets/thank-creators/twitch-icon.svg'}
                              alt="Twitch"
                              className={cn('h-3 w-3', !isLanding && 'sm:h-3.5 sm:w-3.5')}
                            />
                          </a>
                        )}
                        {creator.youtubeLink && (
                          <a
                            href={creator.youtubeLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center"
                          >
                            <img
                              src={creator.youtubeIcon ?? '/assets/thank-creators/youtube-icon.svg'}
                              alt="YouTube"
                              className={cn('h-3 w-3', !isLanding && 'sm:h-3.5 sm:w-3.5')}
                            />
                          </a>
                        )}
                        {(creator.youtubeLink || creator.twitchLink) ? (
                          <a
                            href={creator.youtubeLink || creator.twitchLink || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={cn(
                              'hover:underline',
                              isLanding ? 'text-foreground decoration-foreground/30 underline-offset-2' : 'text-primary'
                            )}
                          >
                            {creator.name}
                          </a>
                        ) : (
                          creator.name
                        )}
                      </span>
                    </p>
                    <div
                      className={cn(
                        'text-muted-foreground',
                        isLanding ? 'space-y-1 text-[0.72rem] leading-snug sm:text-xs' : 'space-y-0.5 text-[11px] sm:text-xs'
                      )}
                    >
                      {creator.quoteLine && (
                        <p className="leading-snug">
                          <span className={cn('italic font-medium', isLanding ? 'text-foreground/80' : 'text-foreground')}>
                            {creator.quoteLine}
                          </span>
                        </p>
                      )}
                      {creator.quoteLine && (
                        (creator.youtubeLink || creator.twitchLink) ? (
                          <a
                            href={creator.youtubeLink || creator.twitchLink || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={cn(
                              'block text-[11px] font-semibold uppercase tracking-wide text-foreground',
                              isLanding ? 'text-left text-foreground/75' : 'text-right'
                            )}
                          >
                            — {creator.name}
                          </a>
                        ) : (
                          <span
                            className={cn(
                              'block text-[11px] font-semibold uppercase tracking-wide text-foreground',
                              isLanding ? 'text-left text-foreground/75' : 'text-right'
                            )}
                          >
                            — {creator.name}
                          </span>
                        )
                      )}
                      <p className="leading-snug">{creator.tojLine}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {hasMultiple && (
        <div className={cn('mt-3 flex justify-center', isLanding ? 'gap-1.5' : 'gap-2')}>
          {specialThanksCreators.map((creator, index) => (
            <button
              key={creator.name}
              type="button"
              onClick={() => handleIndicatorClick(index)}
              className={cn(
                isLanding
                  ? 'h-1.5 w-1.5 rounded-full bg-foreground/25 transition-colors focus:outline-none focus:ring-2 focus:ring-foreground/30 focus:ring-offset-1 focus:ring-offset-background dark:bg-white/25'
                  : 'h-2 w-2 rounded-full bg-muted-foreground/40 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40 focus:ring-offset-1',
                index === currentIndex && (isLanding ? 'bg-foreground dark:bg-white/80' : 'bg-primary')
              )}
              aria-label={`View thanks for ${creator.name}`}
              aria-current={index === currentIndex}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SpecialThanksBox;
